import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Select, Table, Spin } from "antd";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { complianceTableColumns } from "../../util/columnsConfig";
import {
  getChartData,
  getListOfYears,
  getListOfQuarters,
  complianceTableData,
  category,
  TEXT_CONSTANTS,
  getCurrentYear,
  getCurrentQuarter,
} from "../../components/utils/ComplianceConstant";
import UserLayout from "../../component/Layout";
import { userComplianceAnalytics } from "../../services/Service";

const ComplianceDashboard = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [complianceData, setComplianceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState({});
  const [loading, setLoading] = useState(false);
  const token = useSelector((state) => state.auth.token);
  
  const fetchComplianceData = async () => {    
    try {
      const data = await userComplianceAnalytics();
      setLoading(true);
      if (data && Array.isArray(data.data)) {
        setComplianceData(data.data);
        setTotalRecords(data.data.length);
        setLoading(false);
      } else {
        console.error("Expected an array but got:", data);
        setComplianceData([]);
        setTotalRecords(0);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if(!loading) {
      fetchComplianceData();
    }
    
  }, [token]);

  useEffect(() => {
    setSelectedFilter({
      year: getCurrentYear(),
      quarter: getCurrentQuarter(),
      category: category.map((data) => data.value),
    });
  }, []);
  //Dynamic Page Size Options for pagination
  const getPageSizeOptions = () => {
    const defaultOptions = ["10", "20", "50", "100"];
    const validOptions = defaultOptions.filter(
      (size) => Number(size) <= Math.max(totalRecords, 10)
    );
    if (!validOptions.includes(totalRecords.toString())) {
      validOptions.push(totalRecords.toString());
    }
    return validOptions;
  };
  const onChangeHandler = (e, filterOption) => {
    setSelectedFilter({
      ...selectedFilter,
      [filterOption]: e,
    });
  };

  const complianceChartData = useMemo(() => {
    return getChartData(filteredData);
  }, [tableData]);

  const years = useMemo(() => {
    return getListOfYears(complianceData);
  }, [complianceData]);

  const quarters = useMemo(() => {
    return getListOfQuarters(complianceData);
  }, [complianceData]);

  const filterBySelectedOption = (selectedFilter) => {
    let filteredData = complianceData
      .filter(
        (data) => !selectedFilter.year || data.year === selectedFilter.year
      )
      .filter(
        (data) =>
          !selectedFilter.quarter || data.quarter === selectedFilter.quarter
      )
      .map((quarterData) => {
        let categoryData = { ...quarterData };
        if (
          selectedFilter.category &&
          selectedFilter.category?.length !== category?.length
        ) {
          Object.keys(quarterData).map((data) => {
            selectedFilter.category.map((categoryName) => {
              category.map((category) => {
                if (category.value != categoryName) {
                  if (data.includes(category.value)) {
                    delete categoryData[data];
                  }
                }
              });
            });
          });
          return categoryData;
        }
        return quarterData;
      });
    setFilteredData(filteredData);
    setTableData(filteredData);
    setTotalRecords(filteredData.length);
  };

  useEffect(() => {
    filterBySelectedOption(selectedFilter);
  }, [selectedFilter, complianceData]);

  const filterByCompletedStatus = (e, filterBy) => {
    const filterdata = filteredData.filter((data) => data[e.type] === filterBy);
    setTableData(filterdata);
    setTotalRecords(filterdata.length);
  };

  return (
    <UserLayout>
      <main className="p-6 w-full max-w-screen-lg mx-auto">
        <div
          className={`flex-1 transition-all duration-500 rounded-[16px] h-auto`}
        >
          <div className="flex flex-col md:flex-row justify-between items-center bg-[#ffffff] rounded-lg shadow-2xl min-h-20">
            <div className="flex justify-start md:justify-center mt-2 md:ml-4 items-center">
              <span className="text-xl font-bold text-[var(--color-primary)]">
                {TEXT_CONSTANTS.title}
              </span>
            </div>
            <div className="w-full md:w-[50%] flex flex-col md:flex-row justify-around items-center">
              <Select
                showSearch
                value={selectedFilter.year}
                placeholder="Select a year"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={years}
                className="basis-1/4 m-4"
                onChange={(e) => onChangeHandler(e, "year")}
              />
              <Select
                showSearch
                value={selectedFilter.quarter}
                placeholder="Select a quarter"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={quarters}
                className="basis-1/4 m-4"
                onChange={(e) => onChangeHandler(e, "quarter")}
              />
              <Select
                mode="multiple"
                value={selectedFilter.category}
                allowClear
                showSearch
                placeholder="Select a category"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={category}
                className="basis-1/2 m-4"
                onChange={(e) => onChangeHandler(e, "category")}
              />
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <div className="bg-[#ffffff] rounded-lg shadow-2xl h-[300px] mt-4 py-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={300}
                    height={500}
                    data={complianceChartData}
                    layout="vertical"
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="totalUser"
                      domain={[0, filteredData.length]}
                    />
                    <YAxis type="category" width={85} dataKey="categoryName" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="completed"
                      fill="green"
                      activeBar={<Rectangle fill="green" />}
                      onClick={(e) => {
                        filterByCompletedStatus(e, true);
                      }}
                      className="cursor-pointer"
                    >
                      <LabelList dataKey="completed" position="right" />
                    </Bar>

                    <Bar
                      dataKey="notCompleted"
                      fill="red"
                      activeBar={<Rectangle fill="red" />}
                      onClick={(e) => {
                        filterByCompletedStatus(e, false);
                      }}
                      className="cursor-pointer"
                    >
                      <LabelList dataKey="notCompleted" position="right" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-[#ffffff] rounded-lg shadow-2xl mt-4">
                <Table
                  columns={complianceTableColumns}
                  rowKey={(record) => `${record.employeeId}`}
                  dataSource={tableData}
                  pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: totalRecords,
                    showSizeChanger: true,
                    pageSizeOptions: getPageSizeOptions(),
                    position: ["bottomCenter"],
                    onChange: (newPage, newPageSize) => {
                      setPage(newPage);
                      setPageSize(newPageSize);
                    },
                  }}
                  scroll={
                    totalRecords > 0 ? { x: "max-content", y: 400 } : undefined
                  }
                  // Removes scroll when no data
                  sticky={totalRecords > 0 ? { offsetHeader: 0 } : false}
                  tableLayout="fixed"
                  components={{
                    header: {
                      wrapper: (props) => (
                        <thead
                          {...props}
                          className="text-white text-left border bg-[var(--color-primary)]"
                        >
                          {props.children}
                        </thead>
                      ),
                    },
                  }}
                  //   rowSelection={rowSelection}
                  bordered
                  className="border-2 color-[#183D5D] shadow-lg rounded-md w-full"
                />
              </div>
            </>
          )}
        </div>
      </main>
    </UserLayout>
  );
};

export default ComplianceDashboard;
